/**
 * RÉFÉRENTIELS → MOTIFS DE CORRECTION — ce qui EXPLIQUE un écart.
 *
 * POURQUOI CET ONGLET EXISTE, et ce n'est pas seulement un trou de la mesure : l'écran des écarts
 * envoie DÉJÀ l'opérateur ici (« Aucun motif de portée cash_variance au référentiel — ajoutez-en
 * dans Référentiels avant de justifier »), vers un onglet qui n'existait pas. Une consigne qui
 * désigne un endroit vide est une option offerte non implémentée (§23.0).
 *
 * ⚠️ RENOMMER EST SÛR, SUPPRIMER SE PAIE — et c'est une décision du SCHÉMA, pas une limite d'écran.
 * `cash_variance_note.motif_code` POINTE le référentiel au lieu de recopier le libellé, « un libellé
 * figé divergerait du référentiel dès son premier renommage ». Le corollaire est que la suppression
 * fait retomber l'historique sur le CODE brut : « Erreur de rendu monnaie » redevient `ECART-01`.
 * L'écran compte donc ce qui est déjà expliqué, et le DIT avant de laisser supprimer.
 *
 * ⚠️ IL N'Y A PAS DE DÉSACTIVATION ICI, à la différence des moyens de règlement : la table
 * `justification` n'a pas de colonne d'activité. Offrir un interrupteur inexistant serait pire que
 * de ne pas en offrir — l'écran s'en tient donc à ce que la base sait faire.
 *
 * ⚠️ DEUX PORTÉES SEULEMENT SONT IMPLÉMENTÉES, et la liste s'y tient : `cash_variance` est la seule
 * que la journée de caisse accepte ; tout le reste apparaît dans les écarts de stock et de bon.
 * Proposer une portée que personne ne lit créerait des motifs invisibles.
 */
import { useState } from 'react';
import { showToast, Trans } from '@orgazone/ui-web';
import { api, type ReferentialJustification } from '../api';
import { fieldLabel } from '../vocabulary';
import { key, t, type Translatable } from '../i18n';

/**
 * LES PORTÉES OFFERTES — fermée, parce que seules celles-ci sont LUES quelque part.
 * ⚠️ `null` n'est pas « pas de portée » au sens d'un oubli : c'est le cas général, celui des écarts
 *    de stock et de bon. Le nommer évite qu'on le prenne pour un champ laissé vide.
 */
const PORTEES: { readonly v: string | null; readonly l: Translatable }[] = [
  { v: null, l: key('Écarts de stock et de bons') },
  { v: 'cash_variance', l: key('Écarts de caisse') },
];

/** Le sens d'une portée, ou sa valeur brute si elle vient d'ailleurs (reprise, Console). */
function sensPortee(v: string | null): string {
  const connue = PORTEES.find((p) => p.v === v);
  // ⚠️ UNE PORTÉE INCONNUE S'AFFICHE TELLE QUELLE : la Console sème des domaines libres, et la taire
  //    ferait croire que le motif n'en a pas — donc qu'il se comporte comme le cas général.
  return connue ? t(connue.l) : String(v);
}

export function OngletMotifs({ jeu, failure, reload }: {
  jeu: ReferentialJustification[] | null; failure: string | null; reload: () => void;
}) {
  const [composeur, setComposeur] = useState(false);
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [portee, setPortee] = useState<string | null>(null);
  const [armee, setArmee] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const pret = !!code.trim() && !!libelle.trim();

  const creer = async () => {
    setEnCours(true);
    try {
      await api.createReferentielEntry('justification', {
        code: code.trim(), label: libelle.trim(), scope: portee,
        // En queue : un motif neuf ne passe pas devant ceux que l'opérateur connaît.
        sortOrder: (jeu?.length ?? 0) + 1,
      }, `motif:create:${code.trim()}`);
      showToast(t('« {v} » créé — il descendra aux caisses à la prochaine synchronisation.', { v: libelle.trim() }));
      setComposeur(false); setCode(''); setLibelle(''); setPortee(null);
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  const deplacer = async (i: number, vers: number) => {
    if (!jeu || vers < 0 || vers >= jeu.length) return;
    const a = jeu[i]!; const b = jeu[vers]!;
    setEnCours(true);
    try {
      // ⚠️ LES DEUX LIGNES PARTENT ENSEMBLE — tout-ou-rien, sinon deux motifs porteraient le même rang.
      await api.writeMasterData('justification', [
        { code: a.code, sortOrder: b.sortOrder },
        { code: b.code, sortOrder: a.sortOrder },
      ], `motif:order:${a.code}:${b.code}`);
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  const supprimer = async (m: ReferentialJustification) => {
    setEnCours(true);
    try {
      await api.deleteReferentielEntry('justification', { code: m.code }, `motif:delete:${m.code}`);
      showToast(m.usages > 0
        ? t('« {v} » supprimé — les explications déjà posées affichent désormais son code.', { v: m.label })
        : t('« {v} » supprimé — il n’expliquait encore rien.', { v: m.label }));
      setArmee(null);
      reload();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEnCours(false); }
  };

  if (failure) {
    return (
      <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        {t('Le référentiel n’a pas pu être lu : {failure}. Ce n’est pas « aucun motif ».', { failure })}</p>
    );
  }
  if (!jeu) return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture du référentiel…')}</p>;

  const sansCaisse = !jeu.some((m) => m.scope === 'cash_variance');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Motifs de correction')}</div>
          <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            {t('Un écart ne se corrige pas, il s’explique — et l’explication vient d’ici. Renommer un motif suit l’historique ; le supprimer le fait retomber sur son code.')}
          </p>
        </div>
        <button type="button" onClick={() => setComposeur(true)}
          style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-lead)', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}>
          {t('+ Nouveau motif')}
        </button>
      </div>

      {/* ⚠️ LE MANQUE SE DIT AVANT D'ÊTRE RENCONTRÉ : sans motif de caisse, l'écran des écarts
          refusera de justifier, et l'opérateur ne le découvrira que le tiroir ouvert. */}
      {sansCaisse ? (
        <div role="status" style={{ padding: '11px 14px', marginBottom: 14, border: '1px solid var(--amber)', background: 'var(--amber-bg)', borderRadius: 'var(--r-md)', fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.55 }}>
          {t('Aucun motif de portée « Écarts de caisse » : un écart d’espèces ne pourra pas être justifié tant que vous n’en aurez pas créé un.')}
        </div>
      ) : null}

      {composeur ? (
        <div style={{ border: '1px solid var(--teal-300)', background: 'var(--teal-100)', borderRadius: 'var(--r-md)', padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ width: 150 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Code')}</span>
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('ECART-RENDU, CASSE…')}
                style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
            </label>
            <label style={{ flex: 1, minWidth: 190 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{fieldLabel('name')}</span>
              <input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder={t('Erreur de rendu monnaie…')}
                style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }} />
            </label>
            <label style={{ width: 210 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5 }}>{t('Où ce motif est proposé')}</span>
              <select value={portee ?? ''} onChange={(e) => setPortee(e.target.value === '' ? null : e.target.value)}
                style={{ width: '100%', height: 40, padding: '0 10px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}>
                {PORTEES.map((p) => <option key={p.v ?? ''} value={p.v ?? ''}>{t(p.l)}</option>)}
              </select>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" disabled={!pret || enCours} onClick={() => void creer()}
              style={{ height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: pret ? 'var(--teal-800)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: pret ? 'pointer' : 'default' }}>
              {t('Créer le motif')}
            </button>
            <button type="button" onClick={() => { setComposeur(false); setCode(''); setLibelle(''); setPortee(null); }}
              style={{ height: 38, padding: '0 13px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600, cursor: 'pointer' }}>
              {t('Annuler')}
            </button>
          </div>
        </div>
      ) : null}

      {jeu.length === 0 ? (
        <div style={{ padding: '34px 20px', textAlign: 'center', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun motif de correction')}</div>
          <p style={{ margin: '4px auto 0', fontSize: '12.5px', color: 'var(--ink-500)', maxWidth: 420, lineHeight: 1.55 }}>
            {t('Tant que la liste est vide, aucun écart ne peut être expliqué — ni en caisse, ni sur le stock.')}
          </p>
        </div>
      ) : (
        <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {jeu.map((m, i) => (
            <div key={m.code}>
              <div style={{ display: 'grid', gridTemplateColumns: '140px minmax(150px,1fr) minmax(0,170px) 96px 58px 34px', gap: 12, padding: '12px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
                <span style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.code}</span>
                <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</span>
                <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sensPortee(m.scope)}</span>
                <span style={{ fontSize: 'var(--t-dense)', color: m.usages > 0 ? 'var(--ink-700)' : 'var(--ink-400)', whiteSpace: 'nowrap' }}>
                  {m.usages > 0 ? t('{n} explication', { n: m.usages }) : t('jamais employé')}
                </span>
                <span style={{ display: 'flex', gap: 2 }}>
                  <button type="button" disabled={i === 0 || enCours} aria-label={t('Monter {label}', { label: m.label })}
                    onClick={() => void deplacer(i, i - 1)}
                    style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: i === 0 ? 'var(--ink-300)' : 'var(--ink-500)', cursor: i === 0 ? 'default' : 'pointer', padding: 0 }}>↑</button>
                  <button type="button" disabled={i === jeu.length - 1 || enCours} aria-label={t('Descendre {label}', { label: m.label })}
                    onClick={() => void deplacer(i, i + 1)}
                    style={{ width: 26, height: 26, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: i === jeu.length - 1 ? 'var(--ink-300)' : 'var(--ink-500)', cursor: i === jeu.length - 1 ? 'default' : 'pointer', padding: 0 }}>↓</button>
                </span>
                <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {armee !== m.code ? (
                    <button type="button" aria-label={t('Supprimer {label}', { label: m.label })} title={t('Supprimer')} onClick={() => setArmee(m.code)}
                      style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" /></svg>
                    </button>
                  ) : null}
                </span>
              </div>
              {armee === m.code ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '10px 16px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--ink-100)' }}>
                  <span style={{ flex: 1, minWidth: 200, fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.5 }}>
                    {/* ⚠️ LA PHRASE CHANGE SELON CE QUE LA SUPPRESSION COÛTE. Une seule formule pour les
                        deux cas mentirait dans l'un des deux : « rien n'est perdu » est faux dès qu'un
                        écart cite ce motif, et l'avertissement serait du bruit quand rien ne le cite. */}
                    {m.usages > 0
                      ? <Trans k='Supprimer « {label} » ? {n} explication déjà posée affichera <1>son code</1> à la place de son libellé.'
                          vars={{ label: m.label, n: m.usages }} parts={[(x) => <strong>{x}</strong>]} />
                      : <Trans k='Supprimer « {label} » ? Il n’explique encore rien — <1>rien ne sera perdu</1>.'
                          vars={{ label: m.label }} parts={[(x) => <strong>{x}</strong>]} />}
                  </span>
                  <button type="button" disabled={enCours} onClick={() => void supprimer(m)}
                    style={{ height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--rose-fg)', color: '#fff', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>
                    {t('Supprimer')}
                  </button>
                  <button type="button" onClick={() => setArmee(null)}
                    style={{ height: 32, padding: '0 13px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                    {t('Garder')}
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        {t('Un motif se renomme sans risque : l’historique suit, parce qu’il pointe le référentiel au lieu d’en recopier le mot.')}
      </div>
    </div>
  );
}
